import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Rocket, Clock, GitBranch, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * DeployStatusWidget
 *
 * Shows the time-to-ready of the most-recent deploy + a sparkline of
 * the last 5 deploys. Sourced from `/api/admin/deploy-status`, which
 * the backend writes to on every pod boot.
 *
 * Why on the admin dashboard?
 *   • Surfaces deploy regressions (a sudden 2× jump in ready-time is a
 *     red flag) without the admin having to trawl through pod logs.
 *   • Confirms "did the deploy I just kicked off actually happen?" —
 *     the `started_at` row in the history will update.
 *   • Hides the noisy Emergent orchestrator's "Waiting for backend..."
 *     warnings: this widget only counts what our pod actually measured.
 *
 * Failure modes (graceful):
 *   • If the endpoint 403s (non-admin), the widget renders nothing.
 *   • If the collection is empty (very first boot pre-seed), the widget
 *     shows a "first boot" placeholder instead of breaking.
 *   • All errors swallowed silently — this is observability UI, never
 *     a load-blocker for the dashboard.
 */
export function DeployStatusWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/admin/deploy-status?limit=5');
        if (!active) return;
        setData(res.data || null);
      } catch {
        if (active) setErr(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const ackAlert = async () => {
    if (!data?.open_alert?.id) return;
    setAcking(true);
    try {
      await api.post(`/admin/deploy-status/alerts/${data.open_alert.id}/ack`);
      toast.success('Slow-deploy alert acknowledged');
      // Optimistic clear
      setData(d => d ? { ...d, open_alert: null } : d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setAcking(false);
    }
  };

  if (loading) return null;
  if (err || !data) return null;
  if (!data.current && (!data.history || data.history.length === 0)) {
    return null; // first boot — nothing to show yet
  }

  const ms = data.current?.deploy_ready_ms;
  const seconds = ms != null ? (ms / 1000) : null;
  const fmtSeconds = (s) => s == null ? '—' : (s < 10 ? s.toFixed(1) : Math.round(s).toString());

  // Health classification — under 30s is green, 30-90s amber, >90s red.
  const tier = seconds == null ? 'unknown'
    : seconds < 30 ? 'fast'
    : seconds < 90 ? 'slow'
    : 'very-slow';
  const tierColor = {
    fast: 'text-emerald-600 dark:text-emerald-400',
    slow: 'text-amber-600 dark:text-amber-400',
    'very-slow': 'text-rose-600 dark:text-rose-400',
    unknown: 'text-stone-600 dark:text-stone-400',
  }[tier];
  const tierBg = {
    fast: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800',
    slow: 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800',
    'very-slow': 'bg-rose-100 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800',
    unknown: 'bg-stone-100 dark:bg-stone-900/30 border-stone-200 dark:border-stone-800',
  }[tier];

  // Tiny inline sparkline of the last N deploy times (oldest → newest)
  const series = (data.history || []).slice().reverse().map(h => Number(h.deploy_ready_ms || 0) / 1000);
  const maxV = Math.max(1, ...series);
  const minV = Math.min(...series, 0);
  const W = 80;
  const H = 18;
  const points = series.length > 1
    ? series.map((v, i) => {
        const x = (i / (series.length - 1)) * W;
        const y = H - ((v - minV) / Math.max(0.01, maxV - minV)) * H;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ')
    : null;

  // Pod uptime — formatted human-readably
  const upS = data.uptime_seconds || 0;
  const fmtUptime = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  };

  const startedAt = data.current?.started_at
    ? new Date(data.current.started_at).toLocaleString()
    : null;

  // Slow-deploy alert — surfaces when the last N deploys have all
  // exceeded the threshold. Renders an inline banner above the metric
  // with a Dismiss button that calls the ack endpoint.
  const alert = data.open_alert;
  const alertAvgS = alert?.average_ms != null ? (alert.average_ms / 1000).toFixed(1) : null;
  const alertWindow = alert?.window_size || 3;

  return (
    <Card className={`border ${alert ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800' : tierBg} transition-colors`} data-testid="deploy-status-widget">
      <CardContent className="p-4">
        {alert && (
          <div className="mb-3 flex items-start gap-2 p-2 rounded-md bg-rose-100/80 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800" data-testid="deploy-status-alert">
            <AlertTriangle size={14} className="mt-0.5 text-rose-600 dark:text-rose-300 shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              <div className="font-bold text-rose-900 dark:text-rose-200">
                Slow deploy detected
              </div>
              <div className="text-rose-800 dark:text-rose-300 mt-0.5">
                Last {alertWindow} deploys averaged {alertAvgS}s — investigate.
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[10px] text-rose-700 hover:bg-rose-200 dark:text-rose-300 dark:hover:bg-rose-900/60"
              onClick={ackAlert}
              disabled={acking}
              data-testid="deploy-alert-ack-btn"
            >
              <X size={11} className="mr-0.5" /> {acking ? 'Dismissing…' : 'Dismiss'}
            </Button>
          </div>
        )}
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${tierBg.replace('100', '200').replace('900/30', '900/50')}`}>
            <Rocket size={18} className={tierColor} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-700 dark:text-stone-300">
                Deploy Status
              </h3>
              <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-white/60 dark:bg-stone-900/40">
                {tier === 'fast' ? <><CheckCircle2 size={9} className="mr-1" /> Healthy</>
                  : tier === 'slow' ? <>Slow boot</>
                  : tier === 'very-slow' ? <><AlertTriangle size={9} className="mr-1" /> Investigate</>
                  : 'No data'}
              </Badge>
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className={`text-2xl font-bold tabular-nums ${tierColor}`} data-testid="deploy-ready-seconds">
                {fmtSeconds(seconds)}
              </span>
              <span className="text-xs text-stone-500 dark:text-stone-400">s to ready</span>
              {points && (
                <svg
                  width={W} height={H}
                  className="ml-auto"
                  data-testid="deploy-status-sparkline"
                  aria-label={`Last ${series.length} deploys`}
                >
                  <polyline
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={points}
                    className={tierColor}
                  />
                </svg>
              )}
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-[10px] text-stone-600 dark:text-stone-400 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Clock size={10} /> uptime {fmtUptime(upS)}
              </span>
              {startedAt && (
                <span className="inline-flex items-center gap-1" title={startedAt}>
                  <GitBranch size={10} /> booted {new Date(data.current.started_at).toLocaleTimeString()}
                </span>
              )}
              {series.length > 1 && (
                <span className="inline-flex items-center gap-1" title={`${series.length} previous deploys`}>
                  · last {series.length} avg {(series.reduce((s, v) => s + v, 0) / series.length).toFixed(1)}s
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DeployStatusWidget;
